import React, { useState, useEffect } from "react";
import {
  CircleUser, Lock, Eye, EyeOff,
  Users, CalendarDays, Briefcase, Clock, Wallet, Award, FileText,
  UserCheck, Building2, TrendingUp, ShieldCheck, Heart, ScanFace,
  ClipboardList, BarChart3, Fingerprint, BadgeCheck,
} from "lucide-react";
import api from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";

// HR-themed icons scattered across the hero panel as a subtle, on-brand
// background pattern. Each entry positions one icon (% coords) at a size
// and opacity, so the panel reads as "an HR product" without a heavy image.
const HERO_ICONS = [
  { I: Users,         top: "8%",  left: "12%", size: 46, op: 0.14, rot: -12 },
  { I: CalendarDays,  top: "16%", left: "72%", size: 40, op: 0.12, rot: 8 },
  { I: Wallet,        top: "30%", left: "30%", size: 34, op: 0.10, rot: -6 },
  { I: Briefcase,     top: "26%", left: "86%", size: 30, op: 0.10, rot: 14 },
  { I: Clock,         top: "44%", left: "8%",  size: 38, op: 0.12, rot: 10 },
  { I: Award,         top: "52%", left: "60%", size: 44, op: 0.13, rot: -10 },
  { I: FileText,      top: "62%", left: "20%", size: 30, op: 0.10, rot: 6 },
  { I: UserCheck,     top: "70%", left: "80%", size: 42, op: 0.12, rot: -8 },
  { I: TrendingUp,    top: "80%", left: "40%", size: 36, op: 0.11, rot: 12 },
  { I: ShieldCheck,   top: "86%", left: "70%", size: 32, op: 0.10, rot: -14 },
  { I: ScanFace,      top: "38%", left: "44%", size: 28, op: 0.09, rot: 4 },
  { I: ClipboardList, top: "12%", left: "44%", size: 30, op: 0.10, rot: -4 },
  { I: Heart,         top: "90%", left: "14%", size: 26, op: 0.09, rot: 8 },
  { I: Building2,     top: "58%", left: "92%", size: 28, op: 0.09, rot: -6 },
  { I: BarChart3,     top: "72%", left: "52%", size: 30, op: 0.10, rot: 10 },
  { I: Fingerprint,   top: "22%", left: "20%", size: 30, op: 0.10, rot: -10 },
];

// Feature highlights shown under the tagline on the hero panel.
const FEATURES = [
  { I: ScanFace,      label: "Face & GPS Attendance" },
  { I: Wallet,        label: "Payroll & Compliance" },
  { I: CalendarDays,  label: "Leaves & Regularization" },
  { I: TrendingUp,    label: "Performance & Appraisals" },
];

// Faint HR-icon watermark for the (light) form panel — neutral grey at
// very low opacity so it textures the background without hurting form
// legibility. Sits behind the form via z-index.
const PANEL_ICONS = [
  { I: Users,         top: "6%",  left: "10%", size: 38, op: 0.05, rot: -10 },
  { I: CalendarDays,  top: "14%", left: "78%", size: 34, op: 0.05, rot: 8 },
  { I: Briefcase,     top: "30%", left: "30%", size: 30, op: 0.045, rot: -6 },
  { I: Clock,         top: "44%", left: "84%", size: 36, op: 0.05, rot: 10 },
  { I: Wallet,        top: "58%", left: "12%", size: 32, op: 0.05, rot: -8 },
  { I: Award,         top: "70%", left: "72%", size: 38, op: 0.05, rot: 12 },
  { I: UserCheck,     top: "84%", left: "26%", size: 30, op: 0.045, rot: -4 },
  { I: FileText,      top: "88%", left: "82%", size: 28, op: 0.045, rot: 6 },
  { I: ScanFace,      top: "22%", left: "52%", size: 26, op: 0.04, rot: 4 },
  { I: TrendingUp,    top: "50%", left: "48%", size: 28, op: 0.04, rot: -10 },
  { I: Fingerprint,   top: "76%", left: "50%", size: 26, op: 0.04, rot: 8 },
  { I: ClipboardList, top: "36%", left: "8%",  size: 26, op: 0.045, rot: -6 },
];

export const Login = ({ onLogin }) => {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showP, setShowP] = useState(false);
  const [brand, setBrand] = useState({ logo_url: null, company_name: null });
  // Forgot-password overlay state — replaces the sign-in card in place
  // when open. Three sub-steps: request → verify → reset.
  const [forgotOpen, setForgotOpen] = useState(false);

  // Pull the org's real logo + name (public endpoint) so the login page
  // is branded before sign-in. Falls back to the "zucol." wordmark.
  useEffect(() => {
    api.get("/auth/branding")
      .then(d => setBrand({ logo_url: d?.logo_url || null, company_name: d?.company_name || null }))
      .catch(() => {});
  }, []);

  // Translate machine codes from api.login() into messages an employee
  // actually understands. Raw JS errors like "Cannot read properties of
  // null" or backend strings like "Invalid credentials" both read as
  // "the app is broken" to a non-technical user — we want the message to
  // tell them what to do next.
  const humanize = (err) => {
    switch (err.code) {
      case 'NETWORK':
        return "We can't reach the sign-in server. Check your internet connection, then try again.";
      case 'MISSING_FIELDS':
        return "Please enter both your username and password.";
      case 'BAD_CREDENTIALS': {
        let m = "The username or password you entered is incorrect.";
        if (typeof err.remaining === 'number') {
          if (err.remaining === 0) {
            m += " Sign-in from this device will lock for 15 minutes on the next failed attempt.";
          } else if (err.remaining <= 3) {
            m += ` ${err.remaining} attempt${err.remaining === 1 ? '' : 's'} left before sign-in is temporarily locked for 15 minutes.`;
          }
        }
        return m + ' If you\'ve forgotten your password, use "Forgot password?" below to reset it yourself.';
      }
      case 'LOCKED':
        return `Too many failed sign-in attempts from this device. Please wait about ${err.retryAfterMin || 15} minute${(err.retryAfterMin || 15) === 1 ? '' : 's'} and try again — or use "Forgot password?" below to reset it now instead of waiting.`;
      case 'SERVER':
        return "Something went wrong on our side. Please try again in a moment — if it keeps happening, contact HR.";
      default:
        return err.message || "Sign-in didn't work. Please try again — if it keeps failing, contact HR.";
    }
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!u.trim() || !p) { setErr("Please enter both your username and password."); return; }
    setLoading(true);
    setErr("");
    try {
      const d = await api.login(u, p);
      onLogin(d);
    } catch (err) {
      setErr(humanize(err));
    } finally {
      setLoading(false);
    }
  };

  const logoSrc = brand.logo_url ? uploadUrl(brand.logo_url) : null;

  // Brand wordmark fallback — "zucol." with an accent dot, used when no
  // uploaded logo is configured.
  const Wordmark = ({ dark = false }) => (
    <div className="inline-flex items-baseline font-extrabold tracking-tight" style={{ fontSize: 30 }}>
      <span style={{ color: dark ? K.t0 : "#fff" }}>zucol</span>
      <span style={{ color: dark ? K.p : "#fff", opacity: dark ? 1 : 0.9 }}>.</span>
    </div>
  );

  const Logo = ({ onLight }) =>
    logoSrc
      ? <img src={logoSrc} alt={brand.company_name || "Logo"} style={{ maxHeight: 48, maxWidth: 190 }} className="object-contain" />
      : <Wordmark dark={onLight} />;

  return (
    <div className="flex h-screen w-full overflow-hidden bg-bg">
      {/* ───────────── Left hero panel (md+) ───────────── */}
      <div
        className="hidden md:flex relative flex-col justify-center w-1/2 lg:w-[55%] px-12 lg:px-20 overflow-hidden"
        style={{ background: `linear-gradient(150deg, ${K.p} 0%, #1E40AF 55%, #1E3A8A 120%)` }}
      >
        {/* Scattered HR icon pattern */}
        <div className="absolute inset-0 pointer-events-none select-none">
          {HERO_ICONS.map((h, i) => (
            <div key={i} className="absolute" style={{ top: h.top, left: h.left, transform: `rotate(${h.rot}deg)`, opacity: h.op }}>
              <h.I size={h.size} color="#ffffff" strokeWidth={1.5} />
            </div>
          ))}
        </div>
        {/* Soft glow accents */}
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full" style={{ background: "rgba(255,255,255,0.10)", filter: "blur(40px)" }} />
        <div className="absolute -bottom-32 right-0 w-96 h-96 rounded-full" style={{ background: "rgba(255,255,255,0.08)", filter: "blur(50px)" }} />

        {/* Hero content */}
        <div className="relative z-10 max-w-[460px]">
          <div className="inline-flex items-center justify-center bg-white rounded-2xl px-5 py-3 shadow-[0_10px_30px_rgba(0,0,0,0.12)] mb-8">
            <Logo onLight />
          </div>
          <h1 className="text-white font-extrabold leading-tight m-0" style={{ fontSize: 38 }}>
            Human Resource<br />Management System
          </h1>
          <p className="text-white/85 mt-4 mb-8 text-[16px] leading-relaxed">
            Attendance, payroll, leaves and performance — your entire people
            operation, in one clean place.
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-[420px]">
            {FEATURES.map((f, i) => (
              <div key={i} className="flex items-center gap-[10px] bg-white/12 backdrop-blur-sm rounded-xl px-3 py-[10px] border border-white/15">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 flex-shrink-0">
                  <f.I size={16} color="#fff" />
                </div>
                <span className="text-white text-[14px] font-semibold leading-tight">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-6 left-12 lg:left-20 text-white/55 text-[14px] z-10">
          © {new Date().getFullYear()} {brand.company_name || "Zucol"} · All rights reserved
        </div>
      </div>

      {/* ───────────── Right form panel ───────────── */}
      <div className="flex flex-col items-center justify-center w-full md:w-1/2 lg:w-[45%] px-5 sm:px-8 bg-bg1 relative overflow-hidden">
        {/* Faint HR-icon watermark behind the form */}
        <div className="absolute inset-0 pointer-events-none select-none">
          {PANEL_ICONS.map((h, i) => (
            <div key={i} className="absolute" style={{ top: h.top, left: h.left, transform: `rotate(${h.rot}deg)`, opacity: h.op }}>
              <h.I size={h.size} color={K.t0} strokeWidth={1.5} />
            </div>
          ))}
        </div>

        {/* Mobile-only logo (hero is hidden on small screens) */}
        <div className="md:hidden mb-7 flex items-center justify-center relative z-10">
          <div className="inline-flex items-center justify-center rounded-2xl px-4 py-2"
               style={{ background: logoSrc ? "transparent" : `linear-gradient(135deg,${K.p},#1E3A8A)` }}>
            {logoSrc ? <Logo onLight /> : <Wordmark dark={false} />}
          </div>
        </div>

        <div className="w-full max-w-[380px] relative z-10">
          <div className="mb-7">
            <h2 className="text-t0 font-extrabold m-0" style={{ fontSize: 24 }}>Welcome back</h2>
            <p className="text-t2 text-[15px] mt-[6px]">Sign in to your {brand.company_name || "Zucol HR"} account</p>
          </div>

          <form onSubmit={submit}>
            <div className="mb-4">
              <label className="block text-[14px] font-semibold text-t2 mb-[6px]">Username</label>
              <div className="relative">
                <CircleUser size={15} className="absolute left-[12px] top-1/2 -translate-y-1/2 text-t3 pointer-events-none" />
                <input
                  type="text"
                  autoComplete="username"
                  placeholder="you@company.in"
                  value={u}
                  onChange={e => setU(e.target.value)}
                  className="w-full pl-[36px] pr-3 py-[11px] bg-bg2 border border-b0 rounded-xl text-t0 text-[15px] outline-none box-border focus:border-[#2563EB] transition-colors"
                />
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-[14px] font-semibold text-t2 mb-[6px]">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-[12px] top-1/2 -translate-y-1/2 text-t3 pointer-events-none" />
                <input
                  type={showP ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={p}
                  onChange={e => setP(e.target.value)}
                  className="w-full pl-[36px] pr-[40px] py-[11px] bg-bg2 border border-b0 rounded-xl text-t0 text-[15px] outline-none box-border focus:border-[#2563EB] transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowP(v => !v)}
                  aria-label={showP ? "Hide password" : "Show password"}
                  className="absolute right-[10px] top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-t3 p-1 flex items-center"
                >
                  {showP ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {err && (
              <div className="flex items-center gap-2 text-[14px] text-r bg-rb border border-rd rounded-lg px-3 py-2 mb-4">
                <ShieldCheck size={14} className="flex-shrink-0" />
                <span>{err}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-[12px] rounded-xl border-0 cursor-pointer text-white text-[15px] font-bold transition-opacity disabled:opacity-60"
              style={{ background: `linear-gradient(135deg,${K.p},#1E3A8A)`, boxShadow: "0 8px 20px rgba(37,99,235,0.30)" }}
            >
              {loading ? "Signing in…" : <><BadgeCheck size={16} /> Sign In</>}
            </button>
          </form>

          <div className="mt-3 text-center">
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              className="text-[14px] text-p font-semibold bg-transparent border-0 cursor-pointer hover:underline"
            >
              Forgot password?
            </button>
          </div>

          <div className="mt-5 flex items-center gap-2 justify-center text-t3 text-[14px]">
            <ShieldCheck size={12} />
            <span>Secure sign-in · Your session is encrypted</span>
          </div>
          {forgotOpen && <ForgotPasswordPanel initialUsername={u} onClose={() => setForgotOpen(false)} onDone={() => { setForgotOpen(false); setErr(""); }} />}
        </div>

        <div className="md:hidden absolute bottom-5 text-t3 text-[14px]">
          © {new Date().getFullYear()} {brand.company_name || "Zucol"}
        </div>
      </div>
    </div>
  );
};

// Forgot-password inline overlay. Three steps:
//   1. request  → POST /auth/forgot-password { username }
//   2. verify   → POST /auth/verify-otp      { username, otp }  → reset_token
//   3. reset    → POST /auth/reset-password-with-token { username, reset_token, new_password }
// Rendered as a full-card overlay on top of the sign-in form so the
// user doesn't lose context. Backend deliberately returns generic
// success on step 1 (no account-existence disclosure); UX matches.
const ForgotPasswordPanel = ({ initialUsername, onClose, onDone }) => {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState(initialUsername || "");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const post = async (path, body) => {
    const r = await fetch(`${(process.env.REACT_APP_API_URL || 'http://localhost:5000')}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  const doRequest = async (e) => {
    e && e.preventDefault();
    if (!username.trim()) { setErr('Enter your work email or username.'); return; }
    setBusy(true); setErr(""); setInfo("");
    try {
      await post('/auth/forgot-password', { username: username.trim() });
      setInfo('If an account exists, a 6-digit code has been sent to the registered email. Check your inbox — including spam. Code expires in 15 minutes.');
      setStep(2);
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  };

  const doVerify = async (e) => {
    e && e.preventDefault();
    if (!/^\d{6}$/.test(otp)) { setErr('Enter the 6-digit code.'); return; }
    setBusy(true); setErr(""); setInfo("");
    try {
      const r = await post('/auth/verify-otp', { username: username.trim(), otp });
      setResetToken(r.reset_token);
      setInfo('Code verified. Choose a new password.');
      setStep(3);
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e && e.preventDefault();
    if (pw.length < 8) { setErr('New password must be at least 8 characters.'); return; }
    if (!/[A-Za-z]/.test(pw) || !/\d/.test(pw)) { setErr('New password must include at least one letter and one number.'); return; }
    if (pw !== pw2) { setErr('Passwords do not match.'); return; }
    setBusy(true); setErr(""); setInfo("");
    try {
      await post('/auth/reset-password-with-token', {
        username: username.trim(), reset_token: resetToken, new_password: pw,
      });
      setInfo('Password updated. You can now sign in with the new password.');
      setStep(4);
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/40 z-[50] flex items-center justify-center p-3" onMouseDown={() => !busy && onClose()}>
      <div className="animate-modalIn bg-bg1 border border-b0 rounded-2xl p-6 w-full max-w-[600px] shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[17px] font-extrabold text-t0">
            {step === 1 && 'Reset your password'}
            {step === 2 && 'Enter the code'}
            {step === 3 && 'Choose a new password'}
            {step === 4 && 'Done'}
          </div>
          <button onClick={onClose} disabled={busy} className="bg-transparent border-0 text-t3 cursor-pointer text-[15px]">✕</button>
        </div>

        {step === 1 && (
          <form onSubmit={doRequest}>
            <div className="text-[14px] text-t2 mb-3 leading-[1.5]">
              Enter your work email. We'll send a 6-digit code to your registered email address.
            </div>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)}
              placeholder="you@company.in" autoFocus
              className="w-full px-3 py-[10px] bg-bg2 border border-b0 rounded-xl text-t0 text-[15px] outline-none focus:border-p mb-3" />
            {err && <div className="text-[14px] text-r bg-rb border border-rd rounded-lg px-3 py-2 mb-3">{err}</div>}
            <button type="submit" disabled={busy} className="w-full py-[10px] rounded-xl border-0 cursor-pointer text-white text-[15px] font-bold disabled:opacity-60"
              style={{ background: `linear-gradient(135deg,${K.p},#1E3A8A)` }}>
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={doVerify}>
            <div className="text-[14px] text-t2 mb-3 leading-[1.5]">{info}</div>
            <input type="text" inputMode="numeric" pattern="\d{6}" maxLength={6}
              value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="6-digit code" autoFocus
              className="w-full px-3 py-[10px] bg-bg2 border border-b0 rounded-xl text-t0 text-[17px] text-center tracking-[0.4em] font-mono outline-none focus:border-p mb-3" />
            {err && <div className="text-[14px] text-r bg-rb border border-rd rounded-lg px-3 py-2 mb-3">{err}</div>}
            <button type="submit" disabled={busy} className="w-full py-[10px] rounded-xl border-0 cursor-pointer text-white text-[15px] font-bold disabled:opacity-60"
              style={{ background: `linear-gradient(135deg,${K.p},#1E3A8A)` }}>
              {busy ? 'Verifying…' : 'Verify code'}
            </button>
            <button type="button" onClick={() => setStep(1)} disabled={busy} className="w-full mt-2 py-[8px] text-[14px] text-t3 bg-transparent border-0 cursor-pointer">
              Didn't get a code? Try again
            </button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={doReset}>
            <div className="text-[14px] text-t2 mb-3 leading-[1.5]">{info}</div>
            <input type="password" value={pw} onChange={e => setPw(e.target.value)}
              placeholder="New password" autoFocus autoComplete="new-password"
              className="w-full px-3 py-[10px] bg-bg2 border border-b0 rounded-xl text-t0 text-[15px] outline-none focus:border-p mb-2" />
            <input type="password" value={pw2} onChange={e => setPw2(e.target.value)}
              placeholder="Confirm new password" autoComplete="new-password"
              className="w-full px-3 py-[10px] bg-bg2 border border-b0 rounded-xl text-t0 text-[15px] outline-none focus:border-p mb-2" />
            <div className="text-[14px] text-t3 mb-3">At least 8 characters, including a letter and a number.</div>
            {err && <div className="text-[14px] text-r bg-rb border border-rd rounded-lg px-3 py-2 mb-3">{err}</div>}
            <button type="submit" disabled={busy} className="w-full py-[10px] rounded-xl border-0 cursor-pointer text-white text-[15px] font-bold disabled:opacity-60"
              style={{ background: `linear-gradient(135deg,${K.p},#1E3A8A)` }}>
              {busy ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}

        {step === 4 && (
          <div>
            <div className="text-[14px] text-g bg-gb border border-gd rounded-lg px-3 py-3 mb-3">
              ✓ Password updated. You can now sign in with your new password.
            </div>
            <button onClick={onDone} className="w-full py-[10px] rounded-xl border-0 cursor-pointer text-white text-[15px] font-bold"
              style={{ background: `linear-gradient(135deg,${K.p},#1E3A8A)` }}>
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
